import { Component } from '@theme/component';

/**
 * Tabbed section custom element.
 *
 * Tab buttons are rendered server-side in Liquid (one per `_tab` block, each
 * carrying a `data-index` attribute). JS wires the `on:click` handler and
 * manages active state across buttons and panels.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist - Container the tab buttons are rendered into
 * @property {HTMLElement[]} tabPanel - Tab content panels
 *
 * @extends {Component<Refs>}
 */
class TabbedSection extends Component {
  connectedCallback() {
    super.connectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
  }

  /**
   * Called by the on:click binding on each tab button.
   * @param {MouseEvent} event
   */
  activateTab(event) {
    const index = parseInt(/** @type {HTMLElement} */ (event.target).dataset.index ?? '0', 10);
    const tablist = this.refs.tablist;
    const panels = this.refs.tabPanel ?? [];
    const buttons = tablist ? Array.from(tablist.children) : [];

    if (panels.length === 0) return;

    buttons.forEach((button, i) => {
      button.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });

    panels.forEach((panel, i) => {
      const active = i === index;
      panel.hidden = !active;
      panel.style.display = active ? 'block' : '';
    });

    // Carousels in the newly-revealed panel were set up while hidden, so their
    // async visibility tracking can be stuck — force it synchronously rather
    // than waiting on an observer iOS Safari may defer mid-scroll.
    const slideshows = /** @type {NodeListOf<import('@theme/slideshow').Slideshow>} */ (
      panels[index]?.querySelectorAll('slideshow-component') ?? []
    );
    slideshows.forEach((slideshow) => slideshow.syncVisibility());
  }
}

if (!customElements.get('tabbed-section')) {
  customElements.define('tabbed-section', TabbedSection);
}
